import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthContext } from "../AuthContext";
import enterpriseSetupApi from "../services/enterpriseSetupApi";

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const tenantCode = useMemo(() => new URLSearchParams(location.search).get("tenant") || "", [location.search]);
  const redirectTarget = useMemo(() => {
    const rawValue = new URLSearchParams(location.search).get("redirect") || "";
    if (!rawValue) {
      return "";
    }

    try {
      const resolved = new URL(rawValue, window.location.origin);
      if (resolved.origin !== window.location.origin) {
        return "";
      }

      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch (_) {
      return rawValue.startsWith("/") ? rawValue : "";
    }
  }, [location.search]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setContextLoading(true);
    enterpriseSetupApi
      .getPublicContext(tenantCode)
      .then((response) => {
        if (!cancelled) {
          setContext(response?.data || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContext(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setContextLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  useEffect(() => {
    const errorCode = new URLSearchParams(location.search).get("error");
    if (!errorCode) {
      return;
    }

    const messages = {
      sso_domain_not_allowed: "Your SSO account domain is not allowed for this tenant.",
      sso_user_not_provisioned: "Your SSO account is not provisioned yet. Contact an administrator.",
      sso_email_missing: "The SSO provider did not return an email address.",
      sso_no_workspace_access: "SSO succeeded, but the account has no workspace access yet.",
      unsupported_sso_provider: "This SSO provider is not enabled for interactive login.",
      sso_assertion_invalid: "The SAML assertion could not be validated. Review the provider certificate, audience, and ACS configuration.",
    };

    setError(messages[errorCode] || "Single sign-on failed. Please try again.");
  }, [location.search]);

  const tenantProfile = context?.tenant_profile || {};
  const providers = Array.isArray(context?.identity_providers) ? context.identity_providers : [];
  const ssoRequired = Boolean(tenantProfile?.settings?.sso_required);
  const brandPrimary = tenantProfile?.brand_primary || "#2DB838";
  const brandSecondary = tenantProfile?.brand_secondary || "#0E6346";

  const resolveRedirectPath = (loggedInUser) => {
    const role = String(loggedInUser?.role || "").toLowerCase();
    const permissions = Array.isArray(loggedInUser?.permissions) ? loggedInUser.permissions : [];

    if (role === "superadmin") {
      return "/superadmin";
    }

    if (loggedInUser?.is_admin || role === "admin") {
      return "/admin";
    }

    if (permissions.includes("workspace.marketing_access") || role === "marketing") {
      return "/marketing";
    }

    if (permissions.includes("workspace.consultant_access") || role === "consultant") {
      return "/consultant";
    }

    if (permissions.includes("omnichannel.access")) {
      return "/omnichannel/inbox";
    }

    return "";
  };

  const isWrongCredentialsError = (err) => {
    const status = err?.response?.status;
    const msg = (err?.response?.data?.message || err?.message || "").toLowerCase();

    return (
      status === 401 ||
      status === 422 ||
      msg.includes("invalid credentials") ||
      msg.includes("unauthorized") ||
      msg.includes("incorrect") ||
      msg.includes("these credentials do not match") ||
      msg.includes("wrong email or password")
    );
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoggingIn(true);

    try {
      const loggedInUser = await login({ email, password, tenant: tenantCode || undefined });
      const redirectPath = redirectTarget || resolveRedirectPath(loggedInUser);

      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else {
        setError(`Your account role ('${loggedInUser?.role || "None"}') is not permitted.`);
      }
    } catch (err) {
      if (isWrongCredentialsError(err)) {
        setError("Wrong email or password.");
      } else {
        setError(err?.response?.data?.message || err?.message || "Server error. Please try again.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const showLocalLogin = !ssoRequired || !providers.length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: `radial-gradient(circle at top left, ${brandPrimary}29, transparent 28%), linear-gradient(180deg, #f3faf4 0%, #eef3ef 100%)`,
        p: 3,
      }}
    >
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          p: 4,
          bgcolor: "rgba(255,255,255,0.94)",
          borderRadius: 3,
          boxShadow: "0 26px 60px -38px rgba(14,99,70,0.34)",
          border: `1px solid ${brandPrimary}24`,
          width: "100%",
          maxWidth: 440,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 1.5 }}>
          <img
            src="/pics/logo.png"
            alt="Company Logo"
            style={{ height: 50, marginBottom: 12, objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 900 }}>
            Inquiry System Login
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {ssoRequired ? (
          <Alert severity="info">
            This tenant requires single sign-on for interactive access.
          </Alert>
        ) : null}

        {showLocalLogin ? (
          <>
            <TextField
              id="email"
              label="Email Address"
              type="email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoggingIn}
              autoComplete="username"
            />

            <TextField
              id="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoggingIn}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "hide password" : "show password"}
                      onClick={() => setShowPassword((current) => !current)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoggingIn || contextLoading}
              sx={{ mt: 1, py: 1.5, bgcolor: brandPrimary, "&:hover": { bgcolor: brandSecondary } }}
            >
              {isLoggingIn ? <CircularProgress size={24} color="inherit" /> : "Log In"}
            </Button>
          </>
        ) : null}

        {providers.length ? (
          <Stack spacing={1}>
            {providers.map((provider) => (
              <Button
                key={provider.slug}
                variant={showLocalLogin ? "outlined" : "contained"}
                fullWidth
                href={provider.login_url}
                sx={
                  showLocalLogin
                    ? { py: 1.3, borderColor: `${brandPrimary}40`, color: brandSecondary }
                    : { py: 1.3, bgcolor: brandPrimary, "&:hover": { bgcolor: brandSecondary } }
                }
              >
                Continue with {provider.name}
              </Button>
            ))}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}
